import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { ProgressBar } from "react-bootstrap";
import apiHandler from "../../js/apiHandler";
import Constants from "../../js/constants";
import AppModal from "../global/AppModal";
import StripeSetupTrigger from "../global/StripeSetupTrigger";
import { fetchOrgDetails } from "../../helpers/helpers";
import { CheckboxInput } from "../global/forms/FormElements";

const STRIPE_SNOOZE_KEY = "co_stripe_snooze_until";
const STRIPE_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const isStripeSnoozed = () =>
	Date.now() < parseInt(localStorage.getItem(STRIPE_SNOOZE_KEY) || "0", 10);

const CurrentOperations = (props) => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [operations, setOperations] = useState([]);
	const [showCompleted, setShowCompleted] = useState(false);
	const [completedOperation, setCompletedOperation] = useState(null);
	const isInitialLoad = useRef(true);
	const initialOperationsEmptyRef = useRef(null);
	const pollIntervalRef = useRef(null);
	// "banner" = collapsed (notification list rows shown by parent),
	// "overlay" = expanded modal with task details.
	const [displayMode, setDisplayMode] = useState("banner");
	const [tasks, setTasks] = useState([]);
	const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
	const [stripeModalOpen, setStripeModalOpen] = useState(false);
	const [stripeRemindLater, setStripeRemindLater] = useState(isStripeSnoozed);

	const completedTasksRef = useRef(0);
	const hasAutoOpenedRef = useRef(false);
	const fetchTasksGenerationRef = useRef(0);
	const [progressPercentage, setProgressPercentage] = useState(0);
	const [welcomeSlideIndex, setWelcomeSlideIndex] = useState(0);
	const [welcomeCarouselPaused, setWelcomeCarouselPaused] = useState(false);
	const welcomeCarouselIntervalRef = useRef(null);

	const handleCloseOverlay = () => {
		setDisplayMode("banner");
	};

	/**
	 * Removes a synthetic task from local state. Real (server-side) tasks are
	 * completed by their own flows (e.g. GuidedReview DELETEs its task on
	 * finish), so this only handles the locally-injected synthetic cases.
	 */
	const markTaskCompleted = useCallback((taskId) => {
		if (!taskId) return;
		setTasks((prev) => prev.filter((t) => (t.id ?? t.task_id) !== taskId));
	}, []);

	const handleShowReviewProperty = (entityId, taskId) => {
		setDisplayMode("banner");
		// Pass the task id so the guided-review page can DELETE it on finish.
		navigate(`/properties/${entityId}/guided-review`, {
			state: { taskId },
		});
	};

	const nextTask = () => {
		if (tasks.length > 0) {
			setCurrentTaskIndex((prevIndex) => (prevIndex + 1) % tasks.length);
		}
	};

	const prevTask = () => {
		if (tasks.length > 0) {
			setCurrentTaskIndex(
				(prevIndex) => (prevIndex - 1 + tasks.length) % tasks.length,
			);
		}
	};

	const goToTask = (index) => {
		setCurrentTaskIndex(index);
	};

	// Task id for the Stripe task that triggered the modal, so we can mark it
	// completed when the modal closes.
	const pendingStripeTaskIdRef = useRef(null);

	const handleShowStripeSetup = (e, taskId) => {
		if (e && e.preventDefault) e.preventDefault();
		pendingStripeTaskIdRef.current = taskId ?? null;
		setStripeModalOpen(true);
	};

	const handleCloseStripeModal = () => {
		setStripeModalOpen(false);
		pendingStripeTaskIdRef.current = null;
		// Don't remove the task here — the modal closes both on confirm
		// (page redirects to Stripe) and on cancel. We can't tell which
		// happened, so leave the task in place. Once Stripe is actually
		// connected, the next fetchTasks() will skip the synthetic-task
		// injection because payment_account_id will be set.
	};

	const fetchTasks = useCallback(async () => {
		if (!props.org) return;
		const generation = ++fetchTasksGenerationRef.current;
		try {
			const response = await apiHandler.get({
				apiName: Constants.ACCAPI.NAME,
				path: `/organizations/${props.org}/tasks`,
			});
			if (fetchTasksGenerationRef.current !== generation) return;
			const fetchedTasks = Array.isArray(response.data) ? response.data : [];

			const hasStripeTask = fetchedTasks.some(
				(t) => t?.task_type === "setup_stripe",
			);
			const shouldInjectStripeTask = !hasStripeTask;
			let nextTasks = fetchedTasks;

			// Inject a synthetic Stripe setup task if Stripe isn't connected/complete
			// and the API didn't already return one.
			if (shouldInjectStripeTask) {
				try {
					const details = await fetchOrgDetails(props.org);
					if (fetchTasksGenerationRef.current !== generation) return;
					const pm = details?.property_manager_details;
					const isConnected = pm?.payment_account_id != null;
					const isPending = pm?.preliminary_payment_account_id != null;
					if (!isConnected) {
						nextTasks = [
							{
								task_id: "synthetic-setup-stripe",
								task_type: "setup_stripe",
								title: isPending
									? "Complete your Stripe setup"
									: "Set up Stripe payouts",
							},
							...nextTasks,
						];
					}
				} catch (e) {
					// Silently ignore; we'll just not inject the synthetic task.
				}
			}

			setTasks(nextTasks);
			setCurrentTaskIndex(0);
		} catch (err) {
			console.error("Failed to fetch tasks:", err);
		}
	}, [props.org]);

	const setProgress = (isDone) => {
		if (isDone) {
			setProgressPercentage(100);
			return;
		}

		const currentCompleted = completedTasksRef.current;
		let progress = 10;
		if (currentCompleted === 1) progress = 30;
		else if (currentCompleted === 2) progress = 40;
		else if (currentCompleted === 3) progress = 60;
		else if (currentCompleted > 3) {
			// Unknown amount of tasks. If it goes beyond 3, inch closer to 100 without ever reaching it
			progress = 60 + 30 * (1 - Math.pow(0.5, currentCompleted - 3));
		}

		setProgressPercentage(progress);
	};

	const fetchCurrentOperations = useCallback(async () => {
		if (!props.org) return;
		try {
			if (isInitialLoad.current) {
				setLoading(true);
			}
			setError(null);

			const response = await apiHandler.get({
				apiName: Constants.ACCAPI.NAME,
				path: `/organizations/${props.org}/current-operations`,
			});

			const newOperations = Array.isArray(response.data) ? response.data : [];

			if (isInitialLoad.current && initialOperationsEmptyRef.current === null) {
				initialOperationsEmptyRef.current = newOperations.length === 0;
				setProgress(false);
				// Fetch tasks on initial load
				fetchTasks();
			}

			// Use functional setState to access previous operations
			setOperations((prevOperations) => {
				const hadPreviousOps = prevOperations.length > 0;
				const currentOp = newOperations[0];
				const previousOp = prevOperations[0];

				// Detect operation completion scenarios:
				// 1. Previous operation exists but new operations array is empty
				// 2. Current operation has error status (either new or transitioned)
				// 3. Current operation differs from previous (deep comparison)
				const previousHadError = previousOp?.status === "error";
				const currentHasError = currentOp?.status === "error";
				const operationChanged =
					currentOp &&
					previousOp &&
					JSON.stringify(currentOp) !== JSON.stringify(previousOp);

				// Update progress bar when operation changes
				if (operationChanged) {
					completedTasksRef.current += 1;
					setProgress(false);
				}

				// Operation is completed if:
				// - Had previous ops and now empty, OR
				// - Had previous ops and error status changed, OR
				// - Had previous ops and operation changed, OR
				// - No previous ops but current has error/completed status (immediate failure/completion)
				const operationCompleted =
					(hadPreviousOps &&
						(newOperations.length === 0 ||
							(currentHasError && !previousHadError) ||
							operationChanged)) ||
					(!hadPreviousOps &&
						currentOp &&
						(currentHasError || currentOp.status === "completed"));

				if (operationCompleted) {
					// Only set progress to 100% if there are no more operations
					if (newOperations.length === 0) {
						setProgress(true);
					}
					// Fetch tasks when operation completes
					fetchTasks();

					// Clear polling interval since operation is done
					if (pollIntervalRef.current) {
						clearInterval(pollIntervalRef.current);
						pollIntervalRef.current = null;
					}

					// Show completion message if there's a last operation to show
					if (previousOp) {
						// Wait 2 seconds for progress bar animation to complete
						setTimeout(() => {
							setCompletedOperation(previousOp);
							setShowCompleted(true);
						}, 2000);
					}
				}

				return newOperations;
			});

			if (isInitialLoad.current) {
				setLoading(false);
				isInitialLoad.current = false;
			}
		} catch (err) {
			console.error("Failed to fetch current operations:", err);
			setError(err);
			if (isInitialLoad.current) {
				setLoading(false);
				isInitialLoad.current = false;
			}
		}
	}, [props.org, fetchTasks]);

	// Reset all state when org changes
	useEffect(() => {
		fetchTasksGenerationRef.current += 1;
		isInitialLoad.current = true;
		initialOperationsEmptyRef.current = null;
		completedTasksRef.current = 0;
		hasAutoOpenedRef.current = false;
		setOperations([]);
		setTasks([]);
		setProgressPercentage(0);
		setShowCompleted(false);
		setCompletedOperation(null);
	}, [props.org]);

	useEffect(() => {
		fetchCurrentOperations();

		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
			}
		};
	}, [props.org, fetchCurrentOperations]);

	// Separate effect for polling - only poll when there are active operations
	useEffect(() => {
		const hasActiveOps = operations.some(
			(op) => op.status !== "completed" && op.status !== "error",
		);

		if (hasActiveOps && props.org) {
			// Start polling
			pollIntervalRef.current = setInterval(() => {
				if (props.org) {
					fetchCurrentOperations();
				}
			}, 3000);
		} else {
			// Stop polling when no active operations
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		}

		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		};
	}, [operations, props.org]);

	// Find the first operation that is not completed
	const activeOperation = operations.find(
		(op) => op.status !== "completed" && op.status !== "error",
	); // TODO: Remove errors from current-operations; adding temporary filter for now.

	// Auto-open modal on initial load when there are pending tasks
	useEffect(() => {
		if (tasks.length > 0 && !hasAutoOpenedRef.current) {
			const onlyStripeTask =
				tasks.length === 1 && tasks[0]?.task_type === "setup_stripe";
			if (onlyStripeTask && isStripeSnoozed()) return;
			hasAutoOpenedRef.current = true;
			setDisplayMode("overlay");
		}
	}, [tasks]);

	// Notify parent about task count changes
	useEffect(() => {
		props.onTaskCountChange?.(tasks.length);
	}, [tasks.length, props.onTaskCountChange]);

	// Welcome carousel auto-advance
	useEffect(() => {
		// Clear any existing interval
		if (welcomeCarouselIntervalRef.current) {
			clearInterval(welcomeCarouselIntervalRef.current);
			welcomeCarouselIntervalRef.current = null;
		}

		// Start new interval if not paused
		if (!welcomeCarouselPaused) {
			welcomeCarouselIntervalRef.current = setInterval(() => {
				setWelcomeSlideIndex((prev) => (prev + 1) % 5);
			}, 5000); // Auto-advance every 5 seconds
		}

		return () => {
			if (welcomeCarouselIntervalRef.current) {
				clearInterval(welcomeCarouselIntervalRef.current);
				welcomeCarouselIntervalRef.current = null;
			}
		};
	}, [welcomeCarouselPaused]);

	if (loading) {
		return null;
	}

	if (error) {
		return null;
	}

	const renderWelcomeCarousel = () => {
		const slides = [
			{
				title: "Seamless Distribution to Major Booking Sites",
				content:
					"Your properties are automatically distributed to all major booking platforms from this centralized portal. Maximize your reach and bookings effortlessly.",
				icon: "🌐",
			},
			{
				title: "Unified Reservations & Calendar Management",
				content:
					"Manage all your bookings in one place. Real-time calendar syncing prevents double bookings and keeps your availability up-to-date across all channels.",
				icon: "📅",
			},
			{
				title: "Automated Guest Communication",
				content:
					"Streamline guest interactions with automated messages, check-in instructions, and custom guestbooks. Provide exceptional service while saving time.",
				icon: "💬",
			},
			{
				title: "Advanced Technology & Analytics",
				content:
					"Leverage powerful tools including dynamic pricing, performance analytics, and market insights to optimize your revenue and stay competitive.",
				icon: "📈",
			},
			{
				title: "Direct Payouts & Financial Control",
				content:
					"Connect your Stripe account for secure, direct payouts. Track earnings, manage fees, and maintain full financial transparency across all your properties.",
				icon: "💰",
			},
		];

		const currentSlide = slides[welcomeSlideIndex];

		const handleSlideClick = (index) => {
			setWelcomeSlideIndex(index);
			setWelcomeCarouselPaused(true);
		};

		return (
			<div className="co-welcome-wrap">
				<h3 className="co-welcome-title">
					Welcome To Your Property Management Portal!
				</h3>
				<div
					className="co-welcome-card"
					onMouseEnter={() => setWelcomeCarouselPaused(true)}
					onMouseLeave={() => setWelcomeCarouselPaused(false)}>
					<div className="co-welcome-card__content">
						<div className="co-welcome-card__body">
							<h4 className="co-welcome-card__slide-title">
								<span className="co-welcome-card__icon">
									{currentSlide.icon}
								</span>
								{currentSlide.title}
							</h4>
							<p className="co-welcome-card__slide-text">
								{currentSlide.content}
							</p>
						</div>
					</div>

					{/* Carousel indicators */}
					<div className="co-welcome-indicators">
						{slides.map((_, index) => (
							<button
								key={index}
								onClick={() => handleSlideClick(index)}
								className={`co-welcome-dot${index === welcomeSlideIndex ? " co-welcome-dot--active" : ""}`}
								aria-label={`Go to slide ${index + 1}`}
							/>
						))}
					</div>
				</div>
			</div>
		);
	};

	const handleStripeRemindLater = (checked) => {
		setStripeRemindLater(checked);
		if (checked) {
			localStorage.setItem(
				STRIPE_SNOOZE_KEY,
				String(Date.now() + STRIPE_SNOOZE_MS),
			);
		} else {
			localStorage.removeItem(STRIPE_SNOOZE_KEY);
		}
	};

	const renderTasksSection = () => {
		if (tasks.length === 0) return null;

		return (
			<>
				<StripeSetupTrigger
					open={stripeModalOpen}
					onClose={handleCloseStripeModal}
				/>
				<div className="co-tasks-section">
					<h4 className="co-tasks-title">
						{activeOperation
							? "While you're waiting, please complete the next step"
							: "Please review & complete the next step"}
						{tasks.length > 1 ? "s" : ""}:
					</h4>

					<div className="co-tasks-list">
						<div className="co-task-card">
							{(() => {
								const allTasks = [...tasks];
								const totalTasks = allTasks.length;
								const currentTask = allTasks[currentTaskIndex];

								return (
									<>
										<div className="co-task-card__header">
											<h5 className="co-task-card__title">
												🏡 {currentTask?.title || "Task"}
											</h5>
											<div className="co-task-card__counter">
												{currentTaskIndex + 1} / {totalTasks}
											</div>
										</div>

										{totalTasks > 1 && (
											<>
												<button
													onClick={prevTask}
													className="co-task-nav-btn co-task-nav-btn--prev"
													aria-label="Previous task">
													‹
												</button>
												<button
													onClick={nextTask}
													className="co-task-nav-btn co-task-nav-btn--next"
													aria-label="Next task">
													›
												</button>
											</>
										)}

										<div className="co-task-card__desc">
											<p className="co-task-card__desc-text">
												{currentTask.description}
											</p>
										</div>

										{currentTask?.task_type === "review" &&
										currentTask?.entity === "property" ? (
											<button
												className="btn btn-primary co-task-card__action"
												onClick={() =>
													handleShowReviewProperty(
														currentTask?.entity_id,
														currentTask?.id || currentTask?.task_id,
													)
												}>
												Review Property
											</button>
										) : currentTask?.task_type === "setup_stripe" ? (
											<button
												className="btn btn-primary co-task-card__action"
												onClick={(e) =>
													handleShowStripeSetup(
														e,
														currentTask?.id || currentTask?.task_id,
													)
												}>
												{currentTask?.action_label || "Set Up Stripe"}
											</button>
										) : currentTask?.url ? (
											<a
												href={currentTask.url}
												className="btn btn-primary co-task-card__action"
												rel="noopener noreferrer"
												target="_blank">
												{currentTask?.action_label || "Open"}
											</a>
										) : null}

										{totalTasks > 1 && (
											<div className="co-task-dots">
												{allTasks.map((task, index) => (
													<button
														key={`${task?.task_id || "task"}-${index}`}
														onClick={() => goToTask(index)}
														className={`co-task-dot${index === currentTaskIndex ? " co-task-dot--active" : ""}`}>
														<span className="co-dot-label">{index + 1}</span>
													</button>
												))}
											</div>
										)}
									</>
								);
							})()}
						</div>
					</div>
				</div>
			</>
		);
	};

	// Renders nc-item rows for embedding in the NotificationCenter dropdown.
	// When the user clicks an item we switch to overlay; show the modal.
	if (displayMode !== "banner") {
		const handleHide = () => setDisplayMode("banner");
		return (
			<AppModal
				show={true}
				onHide={handleHide}
				title={activeOperation ? "Operation in progress" : "Pending Tasks"}
				maxWidth={800}>
				{activeOperation && renderWelcomeCarousel()}
				{activeOperation &&
					activeOperation.status !== "completed" &&
					activeOperation.status !== "error" && (
						<div className="co-progress-wrap">
							<ProgressBar animated now={progressPercentage} />
						</div>
					)}
				{activeOperation && (
					<h3 className="co-dialog__message">
						{activeOperation.message?.message ||
							activeOperation.message?.operation ||
							"Processing..."}
					</h3>
				)}
				{renderTasksSection()}
				{tasks.length === 1 && tasks[0]?.task_type === "setup_stripe" && (
					<div className="co-remind-later">
						<CheckboxInput
							checked={stripeRemindLater}
							onChange={handleStripeRemindLater}
							label="Remind me later"
						/>
					</div>
				)}
			</AppModal>
		);
	}

	const items = [];

	if (showCompleted && completedOperation) {
		items.push(
			<div key="completed" className="nc-item nc-item--success">
				<span className="nc-item__dot" />
				<div className="nc-item__body">
					<div className="nc-item__title">Operation completed</div>
					{completedOperation.message?.message && (
						<div className="nc-item__message">
							{completedOperation.message.message}
						</div>
					)}
				</div>
			</div>,
		);
	}

	if (activeOperation) {
		items.push(
			<button
				key="active-op"
				className="nc-item nc-item--warning nc-item--clickable"
				onClick={() => {
					setDisplayMode("overlay");
					props.onClose?.();
				}}>
				<span className="nc-item__dot" />
				<div className="nc-item__body">
					<div className="nc-item__title">Operation in progress</div>
					<div className="nc-item__message">
						{activeOperation.message?.message ||
							activeOperation.message?.operation ||
							"Processing…"}
					</div>
				</div>
			</button>,
		);
	}

	tasks.forEach((task, taskIdx) => {
		const isStripeTask = task?.task_type === "setup_stripe";
		if (isStripeTask) {
			items.push(
				<button
					key={`${task.id ?? task.task_id ?? "stripe-setup"}-${taskIdx}`}
					className="nc-item nc-item--warning nc-item--clickable"
					onClick={(e) => {
						handleShowStripeSetup(e, task.id ?? task.task_id);
						props.onClose?.();
					}}>
					<span className="nc-item__dot" />
					<div className="nc-item__body">
						<div className="nc-item__title">
							{task.title || "Set up Stripe payouts"}
						</div>
						{task.description && (
							<div className="nc-item__message">{task.description}</div>
						)}
					</div>
				</button>,
			);
		} else {
			items.push(
				<button
					key={`${task.id ?? task.task_id ?? "task"}-${taskIdx}`}
					className="nc-item nc-item--info nc-item--clickable"
					onClick={() => {
						setDisplayMode("overlay");
						props.onClose?.();
					}}>
					<span className="nc-item__dot" />
					<div className="nc-item__body">
						<div className="nc-item__title">{task.title || "Pending task"}</div>
						{task.description && (
							<div className="nc-item__message">{task.description}</div>
						)}
					</div>
				</button>,
			);
		}
	});

	if (items.length === 0 && !stripeModalOpen) return null;

	return (
		<>
			{items}
			<StripeSetupTrigger
				open={stripeModalOpen}
				onClose={handleCloseStripeModal}
			/>
		</>
	);
};

const mapStateToProps = (state) => ({
	org: state.roleManager.org,
	permissions: state.authState.permissions,
});

export default connect(mapStateToProps)(CurrentOperations);
